/**
 * CreaDTF · configurador DTF.
 *
 * Hasta 20 MB, el archivo viaja con el formulario de producto como propiedad
 * de línea (lo guarda Shopify). Si pesa más, se sube por partes a nuestro
 * servidor de archivos grandes (Ajustes del tema → Archivos grandes) y al
 * pedido solo va el enlace, en la misma propiedad «Archivo». Antes de dejar
 * añadirlo al carrito, todo se comprueba en el navegador:
 * - PNG: medidas reales leyendo la cabecera (IHDR) y la resolución (pHYs) sin
 *   decodificar la imagen a tamaño completo; transparencia y semitransparencias
 *   sobre una copia reducida.
 * - PDF: medidas de la página 1 y render transparente con pdf.js (se carga
 *   solo si el cliente sube un PDF).
 * Bloquea el envío con `form.dataset.blocker` (lo lee creadtf.js) mientras
 * falte el archivo, haya errores o avisos sin aceptar.
 */
(() => {
  const root = document.querySelector('[data-dtf-configurator]');
  if (!root) return;

  // Modo «check» (comprobador de la guía): sin formulario de compra; al final
  // ofrece «Imprimir este archivo», que pasa el archivo a la ficha de producto.
  const checkMode = root.dataset.mode === 'check';
  const form = checkMode ? null : document.getElementById(root.dataset.formId);
  const handoff = root.querySelector('[data-cfg-handoff]');
  const MAX_WIDTH_CM = parseFloat(root.dataset.maxWidth) || 58;
  const MAX_BYTES = parseInt(root.dataset.maxBytes, 10) || 20 * 1024 * 1024;
  const LARGE_URL = (root.dataset.largeUrl || '').replace(/\/+$/, '');
  const LARGE_MAX = LARGE_URL ? parseInt(root.dataset.largeMaxBytes, 10) || 0 : 0;
  const PREVIEW_WIDTH = 1000;
  const THUMB_WIDTH = 600;
  const SEMI_LIMIT = 0.15;

  const $ = (selector) => root.querySelector(selector);
  const input = $('[data-cfg-input]');
  const drop = $('[data-cfg-drop]');
  const fileBox = $('[data-cfg-file]');
  const msg = $('[data-cfg-msg]');
  const checksEl = $('[data-cfg-checks]');
  const warningsEl = $('[data-cfg-warnings]');
  const summaryEl = $('[data-cfg-check-summary]');
  const stage = $('[data-cfg-stage]');
  const canvas = $('[data-cfg-canvas]');
  const widthInput = $('[data-cfg-width]');
  const rulerEl = $('[data-cfg-ruler]');
  const dimsEl = $('[data-cfg-dims]');
  const copiesInput = $('[data-cfg-copies]');
  const lengthEl = $('[data-cfg-length]');
  const billedEl = $('[data-cfg-billed]');
  const roomEl = $('[data-cfg-room]');
  const repeatCheck = $('[data-cfg-repeat]');
  const repeatField = $('[data-cfg-repeat-field]');
  const uploadBox = $('[data-cfg-upload]');
  const uploadText = $('[data-cfg-upload-text]');
  const uploadBar = $('[data-cfg-upload-bar]');
  const uploadRetry = $('[data-cfg-upload-retry]');
  const step = (name) => root.querySelector(`[data-cfg-step="${name}"]`);
  const prop = (name) => root.querySelector(`[data-cfg-prop="${name}"]`);
  const quantity = form?.querySelector('[name="quantity"]');
  const minMeters = parseFloat(form?.dataset.min) || 1;

  const ICONS = {
    ok: '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" stroke-width="2"/></svg>',
    warn: '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M8 2l6.5 12h-13z" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M8 6.5v3.5M8 11.5v1" stroke="currentColor" stroke-width="1.6"/></svg>',
    error: '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><circle cx="8" cy="8" r="6.2" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="currentColor" stroke-width="1.6"/></svg>',
  };
  const STATUS_LABEL = { ok: 'OK', warn: 'Revisar', error: 'Corregir' };

  let state = null;
  let runId = 0;

  /* ---------- utilidades ---------- */

  const fmt = (n, digits = 1) =>
    n.toLocaleString('es-ES', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const mb = (bytes) =>
    bytes < 1048576 ? `${bytes ? Math.max(1, Math.round(bytes / 1024)) : 0} KB` : fmt(bytes / 1048576, 1) + ' MB';
  const escapeHtml = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const setBlocker = (text) => {
    if (!form) return;
    if (text) form.dataset.blocker = text;
    else delete form.dataset.blocker;
    form.dtfSync?.();
  };

  const showMsg = (text, kind) => {
    msg.hidden = !text;
    msg.textContent = text || '';
    msg.className = 'cfg__msg' + (kind ? ` cfg__msg--${kind}` : '');
  };

  const setProp = (name, value) => {
    const el = prop(name);
    if (!el) return;
    el.value = value || '';
    el.disabled = !value;
  };

  /* ---------- lectura de archivos ---------- */

  /** Lee IHDR (medidas, tipo de color), pHYs (resolución) y tRNS de un PNG. */
  const readPngInfo = async (file) => {
    const head = new Uint8Array(await file.slice(0, Math.min(file.size, 1 << 20)).arrayBuffer());
    const signature = [137, 80, 78, 71, 13, 10, 26, 10];
    if (!signature.every((b, i) => head[i] === b)) throw new Error('not-png');
    const view = new DataView(head.buffer);
    const info = { dpi: null, hasAlpha: false };
    let offset = 8;
    while (offset + 8 <= head.length) {
      const length = view.getUint32(offset);
      const type = String.fromCharCode(...head.slice(offset + 4, offset + 8));
      if (type === 'IHDR') {
        info.width = view.getUint32(offset + 8);
        info.height = view.getUint32(offset + 12);
        const colorType = head[offset + 17];
        info.hasAlpha = colorType === 4 || colorType === 6;
      } else if (type === 'pHYs' && offset + 17 <= head.length) {
        const perUnitX = view.getUint32(offset + 8);
        if (head[offset + 16] === 1) info.dpi = perUnitX * 0.0254;
      } else if (type === 'tRNS') {
        info.hasAlpha = true;
      } else if (type === 'IDAT' || type === 'IEND') {
        break;
      }
      offset += 12 + length;
    }
    if (!info.width || !info.height) throw new Error('bad-png');
    return info;
  };

  /** Copia reducida del PNG (para comprobar y para la prueba). */
  const decodePng = async (file, width, height) => {
    const targetW = Math.min(PREVIEW_WIDTH, width);
    const targetH = Math.max(1, Math.round((height * targetW) / width));
    let source;
    try {
      source = await createImageBitmap(file, { resizeWidth: targetW, resizeHeight: targetH, resizeQuality: 'high' });
    } catch {
      source = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    }
    const c = document.createElement('canvas');
    c.width = targetW;
    c.height = targetH;
    c.getContext('2d').drawImage(source, 0, 0, targetW, targetH);
    return c;
  };

  let pdfjsPromise = null;
  const loadPdfjs = () => {
    if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
    pdfjsPromise ||= new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = root.dataset.pdfjs;
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = root.dataset.pdfjsWorker;
        resolve(window.pdfjsLib);
      };
      script.onerror = () => reject(new Error('pdfjs'));
      document.head.appendChild(script);
    });
    return pdfjsPromise;
  };

  const readPdf = async (file) => {
    const lib = await loadPdfjs();
    const doc = await lib.getDocument({ data: await file.arrayBuffer() }).promise;
    const page = await doc.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(PREVIEW_WIDTH / base.width, 4);
    const viewport = page.getViewport({ scale });
    const c = document.createElement('canvas');
    c.width = Math.round(viewport.width);
    c.height = Math.round(viewport.height);
    await page.render({ canvasContext: c.getContext('2d'), viewport, background: 'rgba(0,0,0,0)' }).promise;
    return {
      pages: doc.numPages,
      widthCm: (base.width / 72) * 2.54,
      heightCm: (base.height / 72) * 2.54,
      canvas: c,
    };
  };

  /** Proporción de píxeles transparentes, semitransparentes y fondo blanco. */
  const analyseAlpha = (c) => {
    const { data } = c.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, c.width, c.height);
    let transparent = 0;
    let semi = 0;
    let opaque = 0;
    for (let i = 3; i < data.length; i += 4) {
      const a = data[i];
      if (a < 8) transparent++;
      else if (a > 247) opaque++;
      else semi++;
    }
    const total = data.length / 4;
    const pixel = (x, y) => {
      const i = (y * c.width + x) * 4;
      return [data[i], data[i + 1], data[i + 2], data[i + 3]];
    };
    const corners = [pixel(0, 0), pixel(c.width - 1, 0), pixel(0, c.height - 1), pixel(c.width - 1, c.height - 1)];
    return {
      transparentRatio: transparent / total,
      semiRatio: semi / Math.max(1, semi + opaque),
      whiteBackground: corners.every((p) => p[3] > 247 && p[0] > 235 && p[1] > 235 && p[2] > 235),
      empty: (opaque + semi) / total < 0.002,
    };
  };

  const sha256 = async (file) => {
    if (!crypto?.subtle) return '';
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  };

  /* ---------- comprobación ---------- */

  const evaluate = () => {
    const checks = [];
    const warnings = [];
    const errors = [];
    const add = (status, label, value) => checks.push({ status, label, value });
    const s = state;

    add('ok', 'Formato', s.kind === 'pdf' ? 'PDF' : 'PNG');
    add('ok', 'Peso del archivo', mb(s.file.size));

    if (s.widthCm > MAX_WIDTH_CM + 0.05) {
      add('error', 'Ancho de impresión', `${fmt(s.widthCm)} cm · máx. ${MAX_WIDTH_CM} cm`);
      errors.push(`Tu diseño mide ${fmt(s.widthCm)} cm de ancho y el rollo útil es de ${MAX_WIDTH_CM} cm. Reduce el ancho de impresión en el paso 03.`);
    } else {
      add('ok', 'Ancho dentro del rollo', `${fmt(s.widthCm)} / ${MAX_WIDTH_CM} cm`);
    }

    if (s.kind === 'png') {
      s.dpi = s.pxWidth / (s.widthCm / 2.54);
      const dpi = Math.round(s.dpi);
      if (s.dpi >= 280) {
        add('ok', 'Resolución al tamaño final', `${dpi} ppp`);
      } else if (s.dpi >= 150) {
        add('warn', 'Resolución al tamaño final', `${dpi} ppp`);
        warnings.push({ id: 'dpi', text: `La resolución es de ${dpi} ppp (recomendamos 300). Puede verse algo pixelado.` });
      } else {
        add('error', 'Resolución al tamaño final', `${dpi} ppp`);
        errors.push(`La resolución es de ${dpi} ppp: demasiado baja para imprimir con calidad. Sube un archivo más grande o reduce el ancho de impresión.`);
      }
    } else {
      add('ok', 'Resolución', 'Vectorial o incrustada');
    }

    const a = s.alpha;
    if (a.empty) {
      add('error', 'Contenido', 'Vacío');
      errors.push('No hemos encontrado nada que imprimir: el archivo es completamente transparente.');
    } else if ((s.kind === 'png' && !s.hasAlpha) || a.transparentRatio < 0.01) {
      add('warn', 'Fondo transparente', a.whiteBackground ? 'Fondo blanco' : 'Sin transparencia');
      warnings.push({
        id: 'background',
        text: a.whiteBackground
          ? 'Tu archivo tiene fondo blanco: se imprimirá como un rectángulo blanco alrededor del diseño.'
          : 'Tu archivo no tiene zonas transparentes: se imprimirá el rectángulo completo.',
      });
    } else {
      add('ok', 'Fondo transparente', 'OK');
    }

    if (!a.empty && a.semiRatio > SEMI_LIMIT) {
      add('warn', 'Semitransparencias', `${Math.round(a.semiRatio * 100)} %`);
      warnings.push({
        id: 'semi',
        text: 'Hay sombras o degradados semitransparentes. En DTF llevan blanco debajo y pueden verse con borde.',
      });
    } else if (!a.empty) {
      add('ok', 'Semitransparencias', 'OK');
    }

    if (s.pages > 1) {
      add('warn', 'Páginas del PDF', `${s.pages}`);
      warnings.push({ id: 'pages', text: `Tu PDF tiene ${s.pages} páginas: solo imprimiremos la primera.` });
    }

    return { checks, warnings, errors };
  };

  /* ---------- pintado ---------- */

  const renderChecks = (result) => {
    checksEl.innerHTML = result.checks
      .map(
        (c) => `<li class="cfg__check cfg__check--${c.status}">${ICONS[c.status]}<span>${escapeHtml(c.label)}</span>` +
          `<span>${escapeHtml(c.value)} · ${STATUS_LABEL[c.status]}</span></li>`
      )
      .join('');

    const accepted = state.accepted;
    warningsEl.innerHTML =
      result.errors.map((e) => `<p class="cfg__error">${escapeHtml(e)}</p>`).join('') +
      result.warnings
        .map(
          (w) => `<div class="cfg__warn"><p>${escapeHtml(w.text)}</p><div class="check">` +
            `<input type="checkbox" id="CfgWarn-${w.id}" data-cfg-accept="${w.id}"${accepted.has(w.id) ? ' checked' : ''}>` +
            `<label for="CfgWarn-${w.id}">Entiendo el aviso y quiero imprimirlo así.</label></div></div>`
        )
        .join('');

    const ok = result.checks.filter((c) => c.status === 'ok').length;
    summaryEl.textContent =
      `${ok} correctas` +
      (result.warnings.length ? ` · ${result.warnings.length} aviso${result.warnings.length > 1 ? 's' : ''}` : '') +
      (result.errors.length ? ` · ${result.errors.length} error${result.errors.length > 1 ? 'es' : ''}` : '');
  };

  const renderProof = () => {
    const source = state.preview;
    canvas.width = source.width;
    canvas.height = source.height;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(source, 0, 0);
  };

  const renderRuler = () => {
    const width = state.widthCm;
    const stepCm = width > 30 ? 5 : width > 10 ? 2 : 1;
    let html = '';
    for (let cm = 0; cm <= width + 0.001; cm += stepCm) {
      html += `<span style="left:${(cm / width) * 100}%">${cm}</span>`;
    }
    rulerEl.innerHTML = html;
  };

  const heightCm = () => state.widthCm * (state.pxHeight / state.pxWidth);

  const renderMeters = () => {
    const copies = copiesInput ? Math.max(1, Math.round(parseFloat(copiesInput.value) || 1)) : 1;
    const lengthM = (heightCm() / 100) * copies;
    const billed = Math.max(minMeters, Math.ceil(lengthM - 1e-9));
    const room = Math.round((billed - lengthM) * 100);

    if (lengthEl) lengthEl.textContent = `${fmt(lengthM, 2)} m`;
    if (billedEl) billedEl.textContent = `${billed} m`;
    if (roomEl) {
      roomEl.textContent =
        room > 0
          ? `Pagas ${billed} m y tus hojas ocupan ${fmt(lengthM, 2)} m: te caben ${room} cm más de diseños sin coste extra.`
          : 'Aprovechas el 100 % de los metros que pagas.';
    }

    if (quantity) {
      quantity.value = String(billed);
      quantity.dispatchEvent(new Event('input', { bubbles: true }));
    }
    setProp('length', `${fmt(lengthM, 2)} m (${copies} ${copies === 1 ? 'copia' : 'copias'})`);
  };

  const refresh = () => {
    if (!state) return;
    const result = evaluate();
    state.result = result;
    state.accepted = new Set([...state.accepted].filter((id) => result.warnings.some((w) => w.id === id)));
    renderChecks(result);
    renderRuler();
    dimsEl.textContent = `Hoja: ${fmt(state.widthCm)} × ${fmt(heightCm())} cm`;
    renderMeters();

    setProp('size', `${fmt(state.widthCm)} × ${fmt(heightCm())} cm`);
    setProp('dpi', state.kind === 'png' ? `${Math.round(state.dpi)} ppp` : 'PDF');
    setProp('warnings', result.warnings.filter((w) => state.accepted.has(w.id)).map((w) => w.text).join(' | '));
    syncBlocker();
    syncHandoff();
  };

  /** Comprobador: resumen final y botón para llevar el archivo a la compra. */
  const syncHandoff = () => {
    if (!handoff) return;
    handoff.hidden = !state || state.busy;
    if (!state || !state.result) return;
    const { errors, warnings } = state.result;
    const verdict = handoff.querySelector('[data-cfg-verdict]');
    if (verdict) {
      verdict.textContent = errors.length
        ? 'Hay que corregir el archivo antes de imprimirlo.'
        : warnings.length
          ? `Se puede imprimir, con ${warnings.length === 1 ? 'un aviso' : `${warnings.length} avisos`} que tendrás que aceptar.`
          : 'Tu archivo está listo para imprimir.';
      verdict.dataset.state = errors.length ? 'error' : warnings.length ? 'warn' : 'ok';
    }
  };

  /* ---------- Paso del archivo entre páginas (IndexedDB, solo en este navegador) ---------- */

  const PENDING_KEY = 'pendiente';
  const idb = (mode, run) =>
    new Promise((resolve, reject) => {
      const open = indexedDB.open('creadtf', 1);
      open.onupgradeneeded = () => open.result.createObjectStore('archivos');
      open.onerror = () => reject(open.error);
      open.onsuccess = () => {
        const tx = open.result.transaction('archivos', mode);
        const request = run(tx.objectStore('archivos'));
        tx.oncomplete = () => resolve(request && request.result);
        tx.onerror = () => reject(tx.error);
      };
    });
  const savePending = (file) => idb('readwrite', (s) => s.put({ file, at: Date.now() }, PENDING_KEY));
  const takePending = async () => {
    const entry = await idb('readonly', (s) => s.get(PENDING_KEY));
    await idb('readwrite', (s) => s.delete(PENDING_KEY));
    return entry && Date.now() - entry.at < 2 * 60 * 60 * 1000 ? entry.file : null;
  };

  /* ---------- Archivos grandes: subida por partes a nuestro servidor ---------- */

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const resumeKey = (hash) => `creadtf-subida-${hash}`;

  const api = async (path, options = {}) => {
    const response = await fetch(LARGE_URL + path, options);
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(body.error || `http-${response.status}`);
      error.status = response.status;
      error.body = body;
      throw error;
    }
    return body;
  };

  /** Reintenta los cortes de red y los errores del servidor; los 4xx no tienen arreglo reintentando. */
  const retrying = async (run) => {
    for (let attempt = 0; ; attempt++) {
      try {
        return await run();
      } catch (error) {
        const permanent = error.status && error.status < 500 && error.status !== 429 && error.status !== 409;
        if (permanent || attempt >= 5) throw error;
        await wait(1000 * 2 ** attempt);
      }
    }
  };

  const renderUpload = () => {
    const up = state?.upload;
    uploadBox.hidden = !up;
    if (!up) return;
    const pct = Math.floor((up.sent / state.file.size) * 100);
    uploadBar.style.width = `${up.url ? 100 : pct}%`;
    uploadBox.dataset.state = up.error ? 'error' : up.url ? 'done' : 'busy';
    uploadRetry.hidden = !up.error;
    uploadText.textContent = up.error
      ? up.error
      : up.url
        ? `Archivo grande subido (${mb(state.file.size)}). Ya puedes seguir con tu pedido.`
        : `Archivo grande: lo subimos por nuestra vía para archivos grandes · ${pct} % (${mb(up.sent)} de ${mb(state.file.size)})`;
  };

  /** Miniatura pequeña para el carrito y Mi taller (Shopify no puede reducir archivos de fuera). */
  const thumbBlob = () => {
    const source = state.preview;
    const c = document.createElement('canvas');
    c.width = Math.min(THUMB_WIDTH, source.width);
    c.height = Math.max(1, Math.round((source.height * c.width) / source.width));
    c.getContext('2d').drawImage(source, 0, 0, c.width, c.height);
    return new Promise((resolve) => c.toBlob(resolve, 'image/png'));
  };

  const uploadLarge = async (id) => {
    const file = state.file;
    const up = state.upload;
    up.error = '';
    renderUpload();
    syncBlocker();
    try {
      // Reanudar si este navegador ya empezó a subir el mismo archivo (misma huella)
      let session = null;
      let status = null;
      try {
        session = JSON.parse(localStorage.getItem(resumeKey(state.hash)) || 'null');
      } catch (e) {
        session = null;
      }
      if (session) {
        try {
          status = await api(`/api/uploads/${session.id}`, { headers: { 'X-Upload-Token': session.token } });
        } catch (e) {
          session = null;
        }
      }
      if (!session) {
        status = await retrying(() =>
          api('/api/uploads', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: file.name, size: file.size, type: state.kind, sha256: state.hash }),
          })
        );
        session = { id: status.id, token: status.token };
        try {
          localStorage.setItem(resumeKey(state.hash), JSON.stringify(session));
        } catch (e) {
          /* sin almacenamiento: se sube igual, solo que sin reanudar al recargar */
        }
      }
      if (id !== runId) return;

      const headers = { 'X-Upload-Token': session.token };
      const chunk = status.chunkBytes || 4 * 1024 * 1024;
      up.sent = status.received || 0;
      while (!status.complete && up.sent < file.size) {
        const offset = up.sent;
        const next = await retrying(() =>
          api(`/api/uploads/${session.id}/chunk?offset=${offset}`, {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/octet-stream' },
            body: file.slice(offset, Math.min(file.size, offset + chunk)),
          }).catch((error) => {
            // El servidor ya tenía esa parte (respuesta perdida): seguimos desde donde dice
            if (error.status === 409 && typeof error.body?.received === 'number') return error.body;
            throw error;
          })
        );
        if (id !== runId) return;
        up.sent = next.received;
        renderUpload();
        syncBlocker();
      }

      if (!status.complete) {
        status = await retrying(() => api(`/api/uploads/${session.id}/complete`, { method: 'POST', headers }));
      }
      if (id !== runId) return;

      let previewUrl = status.previewUrl;
      if (!previewUrl) {
        try {
          const blob = await thumbBlob();
          previewUrl = (await api(`/api/uploads/${session.id}/preview`, { method: 'POST', headers, body: blob })).previewUrl;
        } catch (e) {
          previewUrl = ''; // sin miniatura el pedido vale igual
        }
      }
      if (id !== runId) return;

      up.url = status.url;
      up.sent = file.size;
      setProp('remote', status.url);
      setProp('preview', previewUrl);
    } catch (error) {
      if (id !== runId) return;
      console.error('[CreaDTF subida grande]', error);
      if (error.status === 404) {
        try {
          localStorage.removeItem(resumeKey(state.hash));
        } catch (e) {
          /* nada */
        }
      }
      up.error =
        error.status === 413
          ? `Tu archivo pesa ${mb(file.size)} y el máximo es ${mb(LARGE_MAX)}. Expórtalo como PDF o como PNG optimizado.`
          : error.status === 422 || error.status === 415
            ? 'El archivo ha llegado dañado. Pulsa «Reintentar» para subirlo de nuevo.'
            : 'Se ha cortado la subida. Comprueba tu conexión y pulsa «Reintentar»: seguirá por donde iba.';
    }
    renderUpload();
    syncBlocker();
  };

  const syncBlocker = () => {
    if (!state) return setBlocker('Falta: subir tu archivo');
    if (state.busy) return setBlocker('Comprobando tu archivo…');
    const { errors, warnings } = state.result;
    if (errors.length) return setBlocker('Corrige los errores del paso 02');
    const pending = warnings.filter((w) => !state.accepted.has(w.id)).length;
    if (pending) return setBlocker(`Falta: aceptar ${pending === 1 ? 'el aviso' : `${pending} avisos`} del paso 02`);
    const up = state.upload;
    if (up && !up.url) {
      return setBlocker(
        up.error ? 'Falta: terminar de subir tu archivo (paso 01)' : `Subiendo tu archivo… ${Math.floor((up.sent / state.file.size) * 100)} %`
      );
    }
    setBlocker('');
  };

  const showSteps = (visible) => {
    ['check', 'proof', 'options'].forEach((name) => {
      if (step(name)) step(name).hidden = !visible;
    });
    drop.hidden = visible;
    fileBox.hidden = !visible;
  };

  const reset = () => {
    runId++;
    state = null;
    input.value = '';
    input.disabled = false;
    uploadBox.hidden = true;
    showSteps(false);
    showMsg('');
    ['size', 'dpi', 'length', 'hash', 'warnings', 'remote', 'preview'].forEach((name) => setProp(name, ''));
    if (quantity) {
      quantity.value = String(minMeters);
      quantity.dispatchEvent(new Event('input', { bubbles: true }));
    }
    setBlocker('Falta: subir tu archivo');
    syncHandoff();
  };

  /* ---------- flujo principal ---------- */

  const handleFile = async (file) => {
    if (!file) return reset();
    const id = ++runId;
    const isPng = /\.png$/i.test(file.name) || file.type === 'image/png';
    const isPdf = /\.pdf$/i.test(file.name) || file.type === 'application/pdf';

    if (!isPng && !isPdf) {
      reset();
      return showMsg('Ese formato no nos vale: sube un PNG con fondo transparente o un PDF.', 'error');
    }
    const large = file.size > MAX_BYTES;
    if (large && file.size > LARGE_MAX) {
      const max = Math.max(MAX_BYTES, LARGE_MAX);
      reset();
      return showMsg(
        `Tu archivo pesa ${mb(file.size)} y el máximo es ${mb(max)}. Expórtalo como PDF o como PNG optimizado (sin capas ni metadatos).`,
        'error'
      );
    }

    showSteps(false);
    state = { busy: true };
    syncHandoff();
    setBlocker('Comprobando tu archivo…');
    showMsg(`Comprobando ${file.name}…`, 'busy');

    try {
      const next = { file, kind: isPdf ? 'pdf' : 'png', accepted: new Set(), pages: 1, busy: false };
      if (next.kind === 'png') {
        const info = await readPngInfo(file);
        next.pxWidth = info.width;
        next.pxHeight = info.height;
        next.hasAlpha = info.hasAlpha;
        const metaWidthCm = info.dpi ? (info.width / info.dpi) * 2.54 : (info.width / 300) * 2.54;
        next.widthCm = Math.min(MAX_WIDTH_CM, Math.round(metaWidthCm * 10) / 10);
        next.preview = await decodePng(file, info.width, info.height);
      } else {
        const pdf = await readPdf(file);
        next.pages = pdf.pages;
        next.pxWidth = pdf.canvas.width;
        next.pxHeight = pdf.canvas.height;
        next.widthCm = Math.round(pdf.widthCm * 10) / 10;
        next.preview = pdf.canvas;
      }
      next.alpha = analyseAlpha(next.preview);
      next.hash = await sha256(file);
      if (id !== runId) return;

      state = next;
      $('[data-cfg-file-name]').textContent = file.name;
      $('[data-cfg-file-meta]').textContent =
        `${mb(file.size)} · ` +
        (state.kind === 'png' ? `${state.pxWidth} × ${state.pxHeight} px` : `PDF · ${state.pages} pág.`);
      widthInput.max = String(Math.max(MAX_WIDTH_CM, state.widthCm));
      widthInput.value = String(state.widthCm);
      setProp('hash', state.hash);
      showMsg('');
      showSteps(true);
      renderProof();
      if (large && !checkMode) {
        // El archivo no viaja con el formulario (Shopify lo rechazaría): solo el enlace
        input.disabled = true;
        state.upload = { sent: 0, url: '', error: '' };
      }
      refresh();
      if (state.upload) uploadLarge(id);
    } catch (error) {
      if (id !== runId) return;
      console.error('[CreaDTF configurador]', error);
      reset();
      showMsg('No hemos podido leer tu archivo. Comprueba que es un PNG o PDF válido y vuelve a intentarlo.', 'error');
    }
  };

  /* ---------- eventos ---------- */

  input.addEventListener('change', () => handleFile(input.files?.[0]));

  ['dragenter', 'dragover'].forEach((type) =>
    drop.addEventListener(type, (event) => {
      event.preventDefault();
      drop.classList.add('is-dragover');
    })
  );
  ['dragleave', 'drop'].forEach((type) =>
    drop.addEventListener(type, () => drop.classList.remove('is-dragover'))
  );
  drop.addEventListener('drop', (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    const transfer = new DataTransfer();
    transfer.items.add(file);
    input.files = transfer.files;
    handleFile(file);
  });

  root.querySelectorAll('[data-cfg-reset], [data-cfg-reset-again]').forEach((button) =>
    button.addEventListener('click', () => {
      reset();
      input.click();
    })
  );

  warningsEl.addEventListener('change', (event) => {
    const id = event.target.dataset.cfgAccept;
    if (!id || !state) return;
    if (event.target.checked) state.accepted.add(id);
    else state.accepted.delete(id);
    setProp('warnings', state.result.warnings.filter((w) => state.accepted.has(w.id)).map((w) => w.text).join(' | '));
    syncBlocker();
  });

  root.querySelectorAll('[data-cfg-bg]').forEach((button) =>
    button.addEventListener('click', () => {
      root.querySelectorAll('[data-cfg-bg]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
      stage.className = `cfg__stage cfg__stage--${button.dataset.cfgBg}`;
    })
  );

  widthInput.addEventListener('change', () => {
    if (!state) return;
    const value = parseFloat(String(widthInput.value).replace(',', '.'));
    if (!value || value <= 0) {
      widthInput.value = String(state.widthCm);
      return;
    }
    state.widthCm = Math.round(value * 10) / 10;
    widthInput.value = String(state.widthCm);
    refresh();
  });

  uploadRetry?.addEventListener('click', () => {
    if (state?.upload && !state.upload.url) uploadLarge(runId);
  });

  copiesInput?.addEventListener('input', () => state && renderMeters());
  copiesInput?.addEventListener('change', () => {
    copiesInput.value = String(Math.max(1, Math.round(parseFloat(copiesInput.value) || 1)));
    if (state) renderMeters();
  });

  repeatCheck?.addEventListener('change', () => {
    repeatField.hidden = !repeatCheck.checked;
    repeatField.querySelector('input').disabled = !repeatCheck.checked;
  });

  // Comprobador → ficha: guarda el archivo en este navegador y abre la compra
  handoff?.querySelector('[data-cfg-handoff-go]')?.addEventListener('click', async (event) => {
    if (!state?.file) return;
    event.preventDefault();
    const href = event.currentTarget.href;
    try {
      await savePending(state.file);
    } catch (e) {
      console.warn('[CreaDTF] No se pudo guardar el archivo para la compra', e);
    }
    window.location.href = href;
  });

  reset();

  // Ficha: si el visitante viene del comprobador, se carga su archivo automáticamente
  if (!checkMode && 'indexedDB' in window) {
    takePending()
      .then((file) => {
        if (!file) return;
        try {
          const transfer = new DataTransfer();
          transfer.items.add(file);
          input.files = transfer.files;
        } catch (e) {
          return;
        }
        handleFile(file);
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      })
      .catch(() => {});
  }
})();
